import { createAppSelector, RootState } from "../configureStore";
import { EntitiesState } from "../reducers/entities";
import type { EntityType, EntityTypeMap } from "../util/entityTypes";

export type {
  EntityType,
  EntityTypeMap,
  UserEntity,
  NetworkEntity,
  DeviceEntity,
  ValueEntity,
  StateEntity,
  PermissionEntity,
  AclEntity,
  DataEntity,
  PurseEntity,
  PointEntity,
} from "../util/entityTypes";

export interface EntitySelector {
  <T extends EntityType>(state: RootState, type: T, options?: EntityOptions): EntityTypeMap[T] | undefined;
}

export interface EntitiesSelector {
  <T extends EntityType>(state: RootState, type?: T, options?: EntityOptions): Array<EntityTypeMap[T]>;
}

type EntityFilter =
  | Record<string, unknown>
  | Array<Record<string, unknown>>
  | string[]
  | string;

type EntityOptionsObject = {
  parent?: { type: EntityType; id: string };
  filter?: EntityFilter;
  ids?: string[];
  limit?: number;
};

export type EntityOptions = string | string[] | EntityOptionsObject;

type EntityBucket = Record<string, any> | undefined;

function isOptionsObject(options: EntityOptions | undefined): options is EntityOptionsObject {
  return typeof options === 'object' && options !== null && !Array.isArray(options);
}

function matchObject(obj1: any, obj2: any): boolean {
  for (let i = 0; i < Object.keys(obj2).length; i += 1) {
    const key = Object.keys(obj2)[i];
    if (Object.prototype.hasOwnProperty.call(obj1, key)) {
      const left = obj1[key];
      const right = obj2[key];
      if (left && right && left.constructor !== right.constructor) {
        return false;
      }
      if (typeof left === 'object') {
        if (!matchObject(left, right)) {
          return false;
        }
      } else if (left !== right) {
        return false;
      }
    } else {
      return false;
    }
  }
  return true;
}

const defaultArr: any[] = [];
const stateSelector = (state: RootState) => state.entities;

const makeStateTypeSelector = () =>
  createAppSelector(
    stateSelector,
    (_: RootState, type: EntityType) => type,
    (entities: EntitiesState, type: EntityType): EntityBucket => entities && entities[type],
  );

const makeParentStateTypeSelector = () =>
  createAppSelector(
    stateSelector,
    (_: RootState, _type: EntityType, options: EntityOptions | undefined) =>
      isOptionsObject(options) ? options.parent?.type : undefined,
    (entities: EntitiesState, type: EntityType | undefined): EntityBucket =>
      type ? entities && entities[type] : undefined,
  );

const makeParentSelector = () => {
  const parentStateTypeSelector = makeParentStateTypeSelector();
  return createAppSelector(
    parentStateTypeSelector,
    (_: RootState, _type: EntityType, options: EntityOptions | undefined) =>
      isOptionsObject(options) ? options.parent?.id : undefined,
    (entities: EntityBucket, id: string | undefined) => {
      if (id && entities) {
        return entities[id];
      }
      return null;
    },
  );
};

export const makeEntitySelector = (): EntitySelector => {
  const stateTypeSelector = makeStateTypeSelector();
  const parentSelector = makeParentSelector();
  return createAppSelector(
    stateTypeSelector,
    parentSelector,
    (_: RootState, type: EntityType) => type,
    (_: RootState, _type: EntityType, options: EntityOptions | undefined) => options,
    (entities: EntityBucket, parent: any, type: string, options: EntityOptions | undefined) => {
      if (entities && options) {
        if (typeof options === 'string') {
          return entities[options];
        }
        if (isOptionsObject(options)) {
          if (options.parent) {
            if (parent && Object.prototype.hasOwnProperty.call(parent, type)) {
              if (Array.isArray(parent[type])) {
                for (let i = 0; i < parent[type].length; i += 1) {
                  const id = parent[type][i];
                  const found = entities[id];
                  if (found && matchObject(found, options.filter || {})) {
                    return found;
                  }
                }
              } else {
                if (options.filter && matchObject(parent[type], options.filter || {})) {
                  return parent[type];
                }
                return undefined;
              }
            }
          } else {
            for (let i = 0; i < Object.keys(entities).length; i += 1) {
              const key = Object.keys(entities)[i];
              const val = entities[key];
              if (matchObject(val, options.filter || {})) {
                return val;
              }
            }
          }
        }
      }
      return undefined;
    },
  );
};

function shallowEqualArrays<T>(arrA: T[] | undefined, arrB: T[] | undefined): boolean {
  if (arrA === arrB) return true;
  if (!arrA || !arrB) return false;
  if (arrB.length !== arrA.length) return false;

  for (let i = 0; i < arrA.length; i += 1) {
    if (arrA[i] !== arrB[i]) return false;
  }

  return true;
}

export const makeEntitiesSelector = (): EntitiesSelector => {
  const stateTypeSelector = makeStateTypeSelector();
  const parentSelector = makeParentSelector();
  let old: any[] = [];

  return createAppSelector(
    stateTypeSelector,
    parentSelector,
    (_: RootState, type: EntityType) => type,
    (_: RootState, _type: EntityType, options: EntityOptions | undefined) => options,
    (entities: EntityBucket, parent: any, type: string, options: EntityOptions = {}) => {
      let result: any[];
      const optionsObject: EntityOptionsObject = isOptionsObject(options) ? options : {};

      if (!entities) {
        if (old.length > 0) return old;
        return result = old = defaultArr;
      }

      let filters: Array<Record<string, unknown>> | undefined;
      let ids: string[] | undefined;
      let lookIn: Record<string, any>;

      if (typeof options === 'string') {
        ids = [options];
      } else if (Array.isArray(options)) {
        ids = options;
      } else {
        ids = optionsObject.ids;
        const filter = optionsObject.filter;
        if (filter) {
          if (Array.isArray(filter)) {
            if (filter.length && typeof filter[0] === 'string') {
              ids = [...(optionsObject.ids || []), ...(filter as string[])];
            } else {
              filters = filter as Array<Record<string, unknown>>;
            }
          } else if (typeof filter === 'string') {
            ids = [...(optionsObject.ids || []), filter];
          } else {
            filters = [filter];
          }
        }
      }

      if (ids) {
        lookIn = {};
        ids.forEach((id: string) => {
          const found = entities[id];
          if (found) {
            lookIn[id] = found;
          }
        });
      } else {
        lookIn = entities;
      }

      if (optionsObject.parent) {
        result = [];
        if (parent && Object.prototype.hasOwnProperty.call(parent, type)) {
          if (filters) {
            filters.forEach((filter) => {
              parent[type].forEach((id: string) => {
                const found = lookIn[id];
                if (found && matchObject(found, filter) && !result.includes(found)) {
                  result.push(found);
                }
              });
            });
          } else {
            parent[type].forEach((id: string) => {
              const found = lookIn[id];
              if (found) {
                result.push(found);
              }
            });
          }
        }
      } else if (filters) {
        result = [];
        filters.forEach((filter) => {
          Object.keys(lookIn).forEach((key) => {
            const val = lookIn[key];
            if (matchObject(val, filter) && !result.includes(val)) {
              result.push(val);
            }
          });
        });
      } else {
        result = Object.values(lookIn);
      }

      result = result.slice(0, optionsObject.limit);
      if (shallowEqualArrays(result, old)) {
        return old;
      }
      old = result;
      return result;
    },
  );
};

export const getUserData = createAppSelector(
  (state: RootState) => state.entities.user,
  (users) => (users ? Object.values(users)[0] : undefined),
);
